<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: v => ['label', 'contact_reason'].includes(v),
  },
  items: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

const chipRef = ref(null);
const isOpen = ref(false);

const isLabel = computed(() => props.type === 'label');
const chipText = computed(() =>
  isLabel.value
    ? t('SLA_REPORTS.FILTER_CHIP.EXCLUDE_LABELS')
    : t('SLA_REPORTS.FILTER_CHIP.CONTACT_REASONS')
);
const dropdownHeader = computed(() =>
  isLabel.value
    ? t('SLA_REPORTS.FILTER_CHIP.EXCLUDED_LABELS')
    : t('SLA_REPORTS.FILTER_CHIP.EXCLUDED_CONTACT_REASONS')
);

const FALLBACK_COLOR = '636f7a';

const pillStyle = item => {
  const raw = isLabel.value ? item.color : item.config?.color;
  const hex = (raw ?? '').replace('#', '');
  const isValid = /^[0-9a-fA-F]{6}$/.test(hex);
  const safeHex = isValid ? hex : FALLBACK_COLOR;

  const r = parseInt(safeHex.slice(0, 2), 16);
  const g = parseInt(safeHex.slice(2, 4), 16);
  const b = parseInt(safeHex.slice(4, 6), 16);

  return {
    background: `rgba(${r},${g},${b},0.15)`,
    border: `1px solid rgba(${r},${g},${b},0.4)`,
    color: `#${safeHex}`,
  };
};

onClickOutside(chipRef, () => {
  isOpen.value = false;
});
</script>

<template>
  <div ref="chipRef" class="relative inline-block">
    <button
      type="button"
      class="inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-all hover:border-white/[0.18] hover:bg-white/10"
      @click.stop="isOpen = !isOpen"
    >
      <fluent-icon
        v-if="isLabel"
        icon="tag"
        size="12"
        class="opacity-50 flex-shrink-0"
      />
      <fluent-icon
        v-else
        icon="chat"
        size="12"
        class="opacity-50 flex-shrink-0"
      />

      <span>{{ chipText }}</span>
      <span class="text-n-slate-10">{{ `(${items.length})` }}</span>

      <svg
        class="size-3 flex-shrink-0 opacity-50 transition-transform duration-150"
        :class="{ 'rotate-180': isOpen }"
        viewBox="0 0 12 12"
        fill="none"
      >
        <path
          d="M2 4l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div
      v-if="isOpen"
      class="absolute left-0 top-full z-50 mt-1.5 min-w-[180px] max-w-[260px] overflow-hidden rounded-xl border-[0.5px] border-white/10 bg-n-solid-3 pb-2.5 shadow-xl"
      @click.stop
    >
      <div
        class="px-3 pb-2 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-n-slate-10"
      >
        {{ dropdownHeader }}
      </div>

      <div class="flex flex-wrap gap-1 px-2 pb-1">
        <span
          v-for="item in items"
          :key="item.id"
          class="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
          :style="pillStyle(item)"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :style="{ background: pillStyle(item).color }"
          />
          {{ isLabel ? item.title : item.name }}
        </span>
      </div>
    </div>
  </div>
</template>
